import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FiArrowRight } from "react-icons/fi";
import SEO from "../components/SEO";
import useReveal from "../hooks/useReveal";
import { SITE } from "../config/site";

const INK = "currentColor", TEAL = "#0d9488", LINE = "rgba(148,164,182,0.4)", MONO = "'JetBrains Mono', monospace";

const StatikVis = () => (
  <div className="svc-vis">
    <div className="vis-bar"><span>Kat planı · 02</span><span>1:200</span></div>
    <div className="vis-stage">
      <svg viewBox="0 0 460 320" xmlns="http://www.w3.org/2000/svg">
        <rect x="60" y="50" width="340" height="230" fill="none" stroke={INK} strokeWidth="1.8" />
        <line x1="60" y1="165" x2="230" y2="165" stroke={INK} strokeWidth="1" />
        <line x1="230" y1="50" x2="230" y2="280" stroke={INK} strokeWidth="1" />
        <line x1="230" y1="220" x2="400" y2="220" stroke={INK} strokeWidth="1" />
        <g fill={TEAL}>
          {[56, 143, 226, 313, 396].map((x) => [46, 161, 276].map((y) => <rect key={`${x}-${y}`} x={x} y={y} width="9" height="9" rx="1" />))}
        </g>
        <rect x="230" y="110" width="42" height="13" fill={TEAL} opacity="0.35" />
        <rect x="188" y="220" width="42" height="13" fill={TEAL} opacity="0.35" />
        <g fontFamily={MONO} fontSize="8" fill="#8595a6"><text x="60" y="42">A</text><text x="226" y="42">C</text><text x="396" y="42">E</text></g>
        <text x="60" y="305" fontFamily={MONO} fontSize="10" fill={TEAL}>Tipik Kat Planı — A Blok</text>
      </svg>
    </div>
    <div className="vis-foot"><div>Kat<b>14</b></div><div>Beton<b>C30/37</b></div><div>Donatı<b>S420</b></div></div>
  </div>
);

const DepremVis = () => {
  const ref = useRef(null);
  const wrap = useRef(null);
  React.useEffect(() => {
    const path = ref.current, w = wrap.current;
    if (!path || !w) return;
    const W = 460, MID = 130, N = 200;
    const env = (x) => (x < 0.18 ? (x / 0.18) * 0.6 : x < 0.55 ? 0.6 + (Math.sin((x - 0.18) * 12) + 1) * 0.2 : Math.max(0, 1 - (x - 0.55) / 0.45));
    const data = []; for (let i = 0; i < N; i++) { const e = env(i / N); data.push((Math.sin(i * 0.42) + Math.sin(i * 0.13 + 1.5) * 0.6 + Math.sin(i * 0.8 + 0.4) * 0.3) * e); }
    let p = 0, raf = 0, tm = 0, stop = false;
    const draw = () => { if (stop) return; p = Math.min(p + 1.5, N); let d = ""; for (let i = 0; i <= p; i++) d += (i === 0 ? "M" : "L") + ((i / N) * W).toFixed(1) + " " + (MID - data[Math.floor(i)] * 90).toFixed(1) + " "; path.setAttribute("d", d); if (p < N) raf = requestAnimationFrame(draw); else tm = window.setTimeout(() => { p = 0; draw(); }, 1500); };
    const io = new IntersectionObserver((es) => es.forEach((e) => { if (e.isIntersecting) { draw(); io.disconnect(); } })); io.observe(w);
    return () => { stop = true; cancelAnimationFrame(raf); window.clearTimeout(tm); io.disconnect(); };
  }, []);
  return (
    <div className="svc-vis" ref={wrap}>
      <div className="vis-bar"><span>Zaman tanım · Kahramanmaraş 2023</span><span>Kayıt</span></div>
      <div className="vis-stage">
        <svg viewBox="0 0 460 260" preserveAspectRatio="none" style={{ width: "100%", height: "100%" }} xmlns="http://www.w3.org/2000/svg">
          <line x1="0" y1="130" x2="460" y2="130" stroke={LINE} strokeDasharray="3 4" strokeWidth="0.6" />
          <path ref={ref} d="" fill="none" stroke={TEAL} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
      <div className="vis-foot"><div>PGA<b>0.42 g</b></div><div>M_w<b>7.4</b></div><div>Süre<b>28.4 s</b></div></div>
    </div>
  );
};

const BimVis = () => (
  <div className="svc-vis">
    <div className="vis-bar"><span>Federated Model · 4 disiplin</span><span>LOD 350</span></div>
    <div className="vis-stage">
      <svg viewBox="0 0 460 300" xmlns="http://www.w3.org/2000/svg">
        <g transform="translate(230,150)" stroke={INK} strokeWidth="0.9" fill="none">
          {[70, 30, -10, -50].map((dy, i) => (
            <polygon key={i} points={`-95,${-20 + dy} 0,${-60 + dy} 95,${-20 + dy} 0,${20 + dy}`} fill={i === 3 ? "rgba(13,148,136,0.14)" : "rgba(13,148,136,0.05)"} />
          ))}
          <line x1="-95" y1="-70" x2="-95" y2="90" /><line x1="95" y1="-70" x2="95" y2="90" /><line x1="0" y1="-110" x2="0" y2="130" />
        </g>
        <g fontFamily={MONO} fontSize="8">
          <circle cx="180" cy="130" r="3.5" fill={TEAL} /><text x="186" y="128" fill={TEAL}>STR</text>
          <circle cx="290" cy="120" r="3.5" fill="#059669" /><text x="296" y="118" fill="#059669">ARC</text>
          <circle cx="250" cy="200" r="3.5" fill="#f59e0b" /><text x="256" y="198" fill="#b45309">MEP</text>
        </g>
      </svg>
    </div>
    <div className="vis-foot"><div>Disiplin<b>4</b></div><div>Çakışma<b>312 → 0</b></div><div>Araç<b>Revit · Tekla</b></div></div>
  </div>
);

const YazilimVis = () => (
  <div className="svc-vis">
    <div className="vis-bar"><span>etex.exe · TBDY 2018</span><span>v3.4.1</span></div>
    <div className="vis-stage" style={{ padding: 0, background: "#0c1a2b", display: "block" }}>
      <div className="term-body" style={{ padding: "22px 24px" }}>
        <div className="tl">$ etex --check tbdy2018 --model proje_A.edb</div>
        <div className="gh">[01] Loading model... <span className="hi">OK</span> (1.3s)</div>
        <div className="gh">[02] Modal analysis... <span className="hi">OK</span></div>
        <div>&nbsp;&nbsp;T₁ = 0.84s · M₁ = 78.4%</div>
        <div className="gh">[03] Drift check (TBDY 4.9.1)...</div>
        <div>&nbsp;&nbsp;δ_i/h_i = <span className="ok">0.0072 ≤ 0.008 ✓</span></div>
        <div className="gh">[04] Capacity design (TS 500)...</div>
        <div>&nbsp;&nbsp;<span className="ok">98 OK</span> · <span className="err">2 over</span></div>
        <div className="gh">[05] Export report...</div>
        <div className="hi">→ hesap_cildi.docx (47 pg)</div>
        <div className="ok">$ Done in 8.4s</div>
      </div>
    </div>
    <div className="vis-foot"><div>Süre<b>8.4 s</b></div><div>Yönetmelik<b>TBDY'18</b></div><div>Çıktı<b>DOCX+XLSX</b></div></div>
  </div>
);

const VISUALS = [StatikVis, DepremVis, BimVis, YazilimVis];
const SVC_IDS = ["statik", "deprem", "bim", "yazilim"];
const SVC_LINKS = ["/iletisim", "/iletisim", "/iletisim", "/yazilimlar"];

export default function Services() {
  const { t } = useTranslation();
  const pageRef = useRef(null);
  useReveal(pageRef, [t]);

  const items = t("services.items", { returnObjects: true });
  const meta = t("services.hero.meta", { returnObjects: true });
  const steps = t("services.method.steps", { returnObjects: true });

  const ld = {
    "@context": "https://schema.org",
    "@type": "Service",
    provider: { "@type": "Organization", name: SITE.name },
    serviceType: items.map((i) => i.title_1 + " " + i.title_hl).join(", "),
    areaServed: "TR",
  };

  return (
    <div ref={pageRef}>
      <SEO title={t("services.meta_title")} description={t("services.meta_desc")} jsonLd={ld} />

      <section className="page-hero blueprint">
        <div className="container ph-inner">
          <span className="eyebrow reveal">{t("services.hero.eyebrow")}</span>
          <h1 className="reveal d1">{t("services.hero.title_1")} <span className="accent">{t("services.hero.title_hl")}</span> {t("services.hero.title_2")}</h1>
          <p className="ph-lede reveal d2">{t("services.hero.lede")}</p>
          <div className="ph-meta reveal d3">
            {meta.map((m, i) => <span className="m" key={i}>{m.k} · <b>{m.v}</b></span>)}
          </div>
        </div>
      </section>

      {items.map((it, i) => {
        const Vis = VISUALS[i];
        return (
          <section className={`section${i % 2 ? " soft" : ""}`} id={SVC_IDS[i]} key={i}>
            <div className="container">
              <div className={`svc-row${i % 2 ? " flip" : ""}`}>
                <div className="svc-text">
                  <span className="eyebrow svc-eyebrow reveal">{it.num} — {it.eyebrow}</span>
                  <h2 className="reveal d1">{it.title_1} <span className="accent">{it.title_hl}</span></h2>
                  <p className="svc-lede reveal d2">{it.lede}</p>
                  <div className="points reveal d2">
                    {it.points.map((p, j) => (
                      <div className="point" key={j}>
                        <span className="pn">{String(j + 1).padStart(2, "0")}</span>
                        <div><h4>{p.h}</h4><p>{p.p}</p></div>
                      </div>
                    ))}
                  </div>
                  <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                    <Link to={SVC_LINKS[i]} className="btn">{it.cta} <FiArrowRight className="arrow" /></Link>
                    {it.cta2 && <Link to="/iletisim" className="btn ghost">{it.cta2}</Link>}
                  </div>
                </div>
                <div className="svc-vis-wrap reveal d1"><Vis /></div>
              </div>
            </div>
          </section>
        );
      })}

      <section className="section mute">
        <div className="container">
          <span className="eyebrow reveal">{t("services.method.eyebrow")}</span>
          <h2 className="h-lg reveal d1" style={{ marginTop: 14 }}>{t("services.method.title")}</h2>
          <div className="method-grid">
            {steps.map((s, i) => (
              <div className="method-cell reveal" style={{ transitionDelay: `${i * 0.06}s` }} key={i}>
                <div className="step">{s.step}</div>
                <h4>{s.title}</h4>
                <p>{s.p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section ink cta-band">
        <div className="blueprint-on-ink" style={{ position: "absolute", inset: 0, opacity: 0.4 }} />
        <div className="container cta-inner">
          <h2 className="h-xl reveal">{t("services.cta.title").split(" ")[0]} <span className="accent">{t("services.cta.title").split(" ").slice(1).join(" ")}</span></h2>
          <div className="cta-row reveal d1">
            <p>{t("services.cta.desc")}</p>
            <div className="cta-actions">
              <Link to="/iletisim" className="btn on-ink">{t("services.cta.primary")} <FiArrowRight className="arrow" /></Link>
              <a href={SITE.whatsapp} target="_blank" rel="noopener noreferrer" className="btn ghost on-ink">{t("services.cta.secondary")}</a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
